import { useState } from 'react';
import { Download, Trash2, RefreshCw, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePWA } from '@/contexts/PWAContext';
import { useThemeStore } from '@/stores/themeStore';
import { useToast } from '@/hooks/use-toast';
import { clearAllData } from '@/db';
import { exportAllData, downloadAsJson } from '@/lib/dataExport';

export function SettingsPage() {
  const { needRefresh, updateServiceWorker } = usePWA();
  const { theme, setTheme } = useThemeStore();
  const { toast } = useToast();
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateServiceWorker(true);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: 'Failed to update the app. Please try again.',
      });
      setIsUpdating(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await exportAllData();
      downloadAsJson(data);
      toast({
        title: 'Export complete',
        description: 'Your data has been exported successfully.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description: 'Failed to export data. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearData = async () => {
    setIsClearing(true);
    try {
      await clearAllData();

      // Clear localStorage for Zustand persisted stores
      localStorage.removeItem('belot-players');
      localStorage.removeItem('belot-teams');
      localStorage.removeItem('belot-active-match');

      toast({
        title: 'Data cleared',
        description: 'All data has been deleted. Reloading...',
      });

      // Reload page to reset all stores
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Clear failed',
        description: 'Failed to clear data. Please try again.',
      });
      setIsClearing(false);
    }
  };

  return (
    <div className="container py-6 space-y-6">
      {/* App Update Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            App Update
          </CardTitle>
        </CardHeader>
        <CardContent>
          {needRefresh ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium text-green-600 dark:text-green-400">
                  Update available
                </p>
                <p className="text-sm text-muted-foreground">
                  A new version is ready to install
                </p>
              </div>
              <Button onClick={handleUpdate} disabled={isUpdating}>
                {isUpdating ? 'Updating...' : 'Update Now'}
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground">App is up to date</p>
          )}
        </CardContent>
      </Card>

      {/* Theme Section */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Choose how Belot Tracker looks</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">
                <div className="flex items-center gap-2">
                  <Sun className="h-4 w-4" />
                  <span>Light</span>
                </div>
              </SelectItem>
              <SelectItem value="dark">
                <div className="flex items-center gap-2">
                  <Moon className="h-4 w-4" />
                  <span>Dark</span>
                </div>
              </SelectItem>
              <SelectItem value="system">
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  <span>System</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Data Management Section */}
      <Card>
        <CardHeader>
          <CardTitle>Data</CardTitle>
          <CardDescription>Manage your app data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleExport}
            disabled={isExporting}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export All Data'}
          </Button>
          <Button
            variant="destructive"
            className="w-full justify-start"
            onClick={() => setIsClearDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear All Data
          </Button>
        </CardContent>
      </Card>

      {/* About Section */}
      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <p className="font-medium">Belot Tracker</p>
            <p className="text-sm text-muted-foreground">
              Track your Belot card game matches and scores offline
            </p>
          </div>
          <p className="text-sm text-muted-foreground">Version 0.1.0</p>
        </CardContent>
      </Card>

      {/* Clear Data Confirmation Dialog */}
      <Dialog open={isClearDialogOpen} onOpenChange={setIsClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Data</DialogTitle>
            <DialogDescription>
              This will permanently delete all players, teams, matches, and game
              history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsClearDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearData}
              disabled={isClearing}
            >
              {isClearing ? 'Clearing...' : 'Clear All Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
